import {
  COMPOSER_MARKDOWN_TRANSFORMERS,
  handleComposerCodeKey,
  formatPairedComposerCode,
  isComposerSelectionInCode,
} from "./ComposerMarkdown";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type ClipboardEvent,
} from "react";
import {
  $createRangeSelection,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isElementNode,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  COPY_COMMAND,
  CUT_COMMAND,
  FORMAT_TEXT_COMMAND,
  KEY_DOWN_COMMAND,
  PASTE_COMMAND,
  TextNode,
} from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $convertSelectionToMarkdownString,
  $convertFromMarkdownString,
  $convertToMarkdownString,
  $generateNodesFromMarkdownString,
} from "@lexical/markdown";
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text";
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from "@lexical/list";
import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $createCodeNode } from "@lexical/code";
import { $setBlocksType } from "@lexical/selection";
import {
  $getClipboardDataFromSelection,
  setLexicalClipboardDataTransfer,
} from "@lexical/clipboard";
import { mergeRegister } from "@lexical/utils";
import {
  ComposerMentionNode,
  mentionTransformer,
  type ComposerMention,
} from "./ComposerMentionNode";

import type { RichTextInputHandle, RichTextInputProps } from "./RichTextInput";
const EMPTY_MENTIONS: ComposerMention[] = [];

export const ComposerEditorBridge = forwardRef<
  RichTextInputHandle,
  RichTextInputProps
>(function EditorBridge(props, ref) {
  const [editor] = useLexicalComposerContext();
  const callbacks = useRef(props);
  callbacks.current = props;
  const transformers = useMemo(
    () => [
      mentionTransformer(props.mentions ?? EMPTY_MENTIONS),
      ...COMPOSER_MARKDOWN_TRANSFORMERS,
    ],
    [props.mentions],
  );
  const transformerRef = useRef(transformers);
  transformerRef.current = transformers;
  const markdown = useRef(props.initialValue);
  const replaceDraft = (value: string) => {
    editor.update(
      () => {
        $convertFromMarkdownString(
          value,
          transformerRef.current,
          undefined,
          true,
        );
        $getRoot().selectEnd();
      },
      { discrete: true },
    );
  };
  useEffect(() => {
    const catalog = new Map(
      (props.mentions ?? []).map((mention) => [mention.reference, mention]),
    );
    editor.update(() => {
      for (const node of $getRoot().getAllTextNodes()) {
        if (node instanceof ComposerMentionNode) {
          const known = catalog.get(node.__reference);
          if (known && node.getTextContent() !== known.label)
            node.setTextContent(known.label);
        }
      }
    });
  }, [editor, props.mentions]);

  // initialValue is a mount-time draft. The chat parent echoes saved drafts
  // asynchronously; importing that echo here would overwrite newer keystrokes.
  // Intentional replacements go through setMarkdown, within one transaction.

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(
          KEY_DOWN_COMMAND,
          (event) => {
            if (event.isComposing || editor.isComposing()) return false;
            const codeHandled = handleComposerCodeKey(event);
            if (codeHandled !== undefined) return codeHandled;
            return callbacks.current.onKeyDown?.(event) ?? false;
          },
          COMMAND_PRIORITY_HIGH,
        ),
        editor.registerNodeTransform(TextNode, (node) => {
          if (!editor.isComposing()) formatPairedComposerCode(node);
        }),
        ...[COPY_COMMAND, CUT_COMMAND].map((command) =>
          editor.registerCommand(
            command,
            (event) => {
              const selection = $getSelection();
              if (
                !(event instanceof window.ClipboardEvent) ||
                !event.clipboardData ||
                !$isRangeSelection(selection)
              )
                return false;
              const data = $getClipboardDataFromSelection(selection);
              // External apps receive the canonical references; internal paste also
              // retains rich formatting and token metadata via Lexical's JSON MIME.
              data["text/plain"] = $convertSelectionToMarkdownString(
                transformerRef.current,
                selection,
                true,
              );
              setLexicalClipboardDataTransfer(event.clipboardData, data);
              event.preventDefault();
              if (command === CUT_COMMAND) selection.removeText();
              return true;
            },
            COMMAND_PRIORITY_HIGH,
          ),
        ),
        editor.registerCommand(
          PASTE_COMMAND,
          (event) => {
            if (!(event instanceof window.ClipboardEvent)) return false;
            callbacks.current.onPaste?.(
              event as unknown as ClipboardEvent<HTMLDivElement>,
            );
            if (event.defaultPrevented) return true;
            const data = event.clipboardData;
            const selection = $getSelection();
            if (
              !data ||
              data.getData("application/x-lexical-editor") ||
              data.getData("text/html") ||
              !$isRangeSelection(selection) ||
              isComposerSelectionInCode()
            )
              return false;
            // Message copy buttons write canonical Markdown as plain text.
            // Import references without changing ordinary literal text pastes.
            const nodes = $generateNodesFromMarkdownString(
              data.getData("text/plain"),
              transformerRef.current,
              true,
            );
            const hasMentions = nodes.some(
              (node) =>
                node instanceof ComposerMentionNode ||
                ($isElementNode(node) &&
                  node
                    .getAllTextNodes()
                    .some((text) => text instanceof ComposerMentionNode)),
            );
            if (!hasMentions) return false;
            selection.insertNodes(nodes);
            event.preventDefault();
            return true;
          },
          COMMAND_PRIORITY_HIGH,
        ),
        editor.registerUpdateListener(
          ({ editorState, dirtyElements, dirtyLeaves }) => {
            editorState.read(() => {
              if (dirtyElements.size || dirtyLeaves.size) {
                const next = $convertToMarkdownString(
                  transformerRef.current,
                  undefined,
                  true,
                );
                if (next !== markdown.current) {
                  markdown.current = next;
                  callbacks.current.onChange(next);
                }
              }
              const selection = $getSelection();
              if (
                $isRangeSelection(selection) &&
                selection.isCollapsed() &&
                !isComposerSelectionInCode()
              ) {
                const before = $createRangeSelection();
                before.anchor.set($getRoot().getKey(), 0, "element");
                before.focus.set(
                  selection.anchor.key,
                  selection.anchor.offset,
                  selection.anchor.type,
                );
                callbacks.current.onSelectionChange?.(before.getTextContent());
              } else callbacks.current.onSelectionChange?.("");
            });
            const element = editor.getRootElement();
            if (element && (dirtyElements.size || dirtyLeaves.size)) {
              // Measuring at auto height removes the scroll range. Preserve the
              // viewport during edits, and avoid measuring selection-only updates.
              const scrollTop = element.scrollTop;
              const height = element.style.height;
              element.style.height = "auto";
              const contentHeight = element.scrollHeight;
              element.style.height = height;
              element.scrollTop = scrollTop;
              callbacks.current.onHeightChange?.(contentHeight);
            }
          },
        ),
      ),
    [editor],
  );

  useImperativeHandle(ref, () => ({
    getMarkdown: () => markdown.current,
    getElement: () => editor.getRootElement(),
    getCaretRect: () => {
      const selection = window.getSelection();
      if (
        !selection?.rangeCount ||
        !editor.getRootElement()?.contains(selection.anchorNode)
      )
        return null;
      const range = selection.getRangeAt(0).cloneRange();
      range.collapse(true);
      const rect = range.getBoundingClientRect();
      return rect.height
        ? rect
        : (selection.anchorNode?.parentElement?.getBoundingClientRect() ??
            null);
    },
    setMarkdown: replaceDraft,
    focus: () => editor.focus(),
    insertMention: (mention) => {
      editor.update(
        () => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || !selection.isCollapsed()) return;
          const anchor = selection.anchor;
          const node = anchor.getNode();
          if (!$isTextNode(node) || node instanceof ComposerMentionNode) return;
          const match = /(?:^|\s)([@/][^\s]*)$/.exec(
            node.getTextContent().slice(0, anchor.offset),
          );
          if (!match) return;
          selection.setTextNodeRange(
            node,
            anchor.offset - match[1].length,
            node,
            anchor.offset,
          );
          const space = $createTextNode(" ");
          selection.insertNodes([new ComposerMentionNode(mention), space]);
          space.selectEnd();
        },
        { discrete: true },
      );
      editor.focus();
    },
    format: (kind) => {
      if (["bold", "italic", "strike", "code"].includes(kind)) {
        editor.dispatchCommand(
          FORMAT_TEXT_COMMAND,
          kind === "strike"
            ? "strikethrough"
            : (kind as "bold" | "italic" | "code"),
        );
      } else if (kind === "link") {
        const url = window.prompt("Link URL");
        if (url) editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
      } else if (
        kind === "list" ||
        kind === "ordered-list" ||
        kind === "task-list"
      ) {
        editor.dispatchCommand(
          kind === "list"
            ? INSERT_UNORDERED_LIST_COMMAND
            : kind === "ordered-list"
              ? INSERT_ORDERED_LIST_COMMAND
              : INSERT_CHECK_LIST_COMMAND,
          undefined,
        );
      } else
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () =>
              kind === "heading"
                ? $createHeadingNode("h2")
                : kind === "quote"
                  ? $createQuoteNode()
                  : $createCodeNode(),
            );
          }
        });
      editor.focus();
    },
  }));
  return null;
});
